import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import {
  Home,
  Search,
  ShoppingBag,
  MessageSquare,
  Briefcase,
  Plus,
  LogOut,
  Calendar,
  ArrowRight,
} from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { ConfirmationDialog } from '../ui/ConfirmationDialog';
import { preloadRoute } from '../../utils/routePrefetch';

interface LeftSidebarProps {
  className?: string;
}

const navItems = [
  { path: '/', icon: Home, label: 'Home' },
  { path: '/explore', icon: Search, label: 'Explore' },
  { path: '/requirements', icon: Briefcase, label: 'Requirements' },
  { path: '/event-matches-page', icon: Calendar, label: 'Event Requirements' },
  { path: '/services', icon: ShoppingBag, label: 'Services' },
  { path: '/messages', icon: MessageSquare, label: 'Messages' },
];

// Premium plan (Apex only)
const premiumPlans = [
  { key: 'apex', label: 'Apex', icon: '💎' }
];

export const LeftSidebar: React.FC<LeftSidebarProps> = ({ className = '' }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const [currentPremiumPlan] = useState(premiumPlans[0]);
  const [isLogoutConfirmOpen, setIsLogoutConfirmOpen] = useState(false);

  const colors = [
    { bg: 'bg-[#8649ED]/10', text: 'text-[#8649ED]', hover: 'hover:bg-[#8649ED]/5' }, // Purple - Home
    { bg: 'bg-[#FF881D]/10', text: 'text-[#FF881D]', hover: 'hover:bg-[#FF881D]/5' }, // Orange - Explore
    { bg: 'bg-[#0BB475]/10', text: 'text-[#0BB475]', hover: 'hover:bg-[#0BB475]/5' }, // Green - Requirements
    { bg: 'bg-[#6366F1]/10', text: 'text-[#6366F1]', hover: 'hover:bg-[#6366F1]/5' }, // Indigo - Event Matches
    { bg: 'bg-[#F59E0B]/10', text: 'text-[#F59E0B]', hover: 'hover:bg-[#F59E0B]/5' }, // Amber - Services
    { bg: 'bg-[#EC4899]/10', text: 'text-[#EC4899]', hover: 'hover:bg-[#EC4899]/5' }  // Pink - Messages
  ];

  return (
    <aside className={`hidden lg:flex flex-col h-screen sticky top-0 overflow-y-auto scrollbar-thin bg-white dark:bg-[#10131D] border-r border-neutral-200 dark:border-white/[0.07] ${className}`}>
      <div className="flex flex-col h-full py-6 px-3 sm:px-4">
        {/* Logo */}
        <Link to="/" className="flex items-center space-x-3 mb-8 px-2 group">
          <motion.div
            className="relative w-12 h-12 rounded-2xl overflow-hidden shadow-lg shadow-purple-200/50 dark:shadow-purple-900/40 shrink-0"
            whileHover={{ scale: 1.05, rotate: 2 }}
            whileTap={{ scale: 0.95 }}
            transition={{ type: "spring", stiffness: 300, damping: 20 }}
          >
            <motion.div
              className="absolute inset-0 bg-gradient-to-br from-[#8649ED] via-[#FF881D] to-[#8549ED] opacity-15 group-hover:opacity-25 transition-opacity duration-300"
              animate={{
                background: [
                  "linear-gradient(45deg, #8649ED, #FF881D, #8549ED)",
                  "linear-gradient(90deg, #8549ED, #8649ED, #FF881D)",
                  "linear-gradient(135deg, #FF881D, #8549ED, #8649ED)"
                ]
              }}
              transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
            />
            <motion.img
              src="/crevvi-logo.png"
              alt="Crevvi Logo"
              className="w-full h-full object-contain relative z-10 filter drop-shadow-sm"
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.2 }}
            />
          </motion.div>
          <motion.div
            className="flex flex-col"
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
          >
            <motion.div
              className="text-xl font-black flex items-center tracking-tight"
              whileHover={{ scale: 1.02, letterSpacing: '0.05em' }}
              transition={{ type: "spring", stiffness: 300, damping: 20 }}
            >
              <motion.span
                style={{ color: '#8649ED' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10 }}
              >
                C
              </motion.span>
              <motion.span
                style={{ color: '#FF881D' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10, delay: 0.05 }}
              >
                r
              </motion.span>
              <motion.span
                style={{ color: '#0BB475' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10, delay: 0.1 }}
              >
                e
              </motion.span>
              <motion.span
                style={{ color: '#3B82F6' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10, delay: 0.15 }}
              >
                v
              </motion.span>
              <motion.span
                style={{ color: '#3B82F6' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10, delay: 0.2 }}
              >
                v
              </motion.span>
              <motion.span
                style={{ color: '#8649ED' }}
                className="inline-block font-black"
                whileHover={{ y: -2, scale: 1.1 }}
                transition={{ type: "spring", stiffness: 400, damping: 10, delay: 0.25 }}
              >
                i
              </motion.span>
            </motion.div>
            <motion.span
              className="text-[10px] font-medium text-gray-600 dark:text-gray-400 tracking-wider uppercase whitespace-nowrap"
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
            >
              Creative Vibes Only
            </motion.span>
          </motion.div>
        </Link>

        {/* Navigation Items */}
        <nav className="flex-1 space-y-1.5">
          {navItems.map((item, index) => {
            const Icon = item.icon;
            const isActive = (() => {
              const fullPath = location.pathname + (location.search || '');
              if (item.path.includes('?')) return fullPath === item.path;
              return location.pathname === item.path;
            })();
            const colorScheme = colors[index];

            return (
              <Link
                key={item.path}
                to={item.path}
                onPointerEnter={() => preloadRoute(item.path)}
                className={`relative flex items-center px-4 py-3 rounded-xl transition-all duration-200 group overflow-hidden ${isActive
                  ? `${colorScheme.bg} ${colorScheme.text} font-semibold shadow-sm border border-purple-500/20`
                  : `text-neutral-700 dark:text-gray-400 hover:bg-neutral-100 dark:hover:bg-white/[0.04] hover:text-neutral-900 dark:hover:text-white`
                  }`}
              >
                <span className="relative z-10 inline-flex items-center">
                  <Icon size={20} />
                </span>
                <span className="relative z-10 ml-3 font-medium text-sm">{item.label}</span>
              </Link>
            );
          })}

          {/* Apex Premium Card */}
          <div className="mt-5 mb-3 p-4 rounded-2xl bg-neutral-50 dark:bg-[#151A25] border border-neutral-200 dark:border-purple-500/20 relative overflow-hidden group shadow-lg shadow-purple-950/20">
            <div className="absolute -top-10 -right-10 w-28 h-28 bg-purple-600/20 rounded-full blur-2xl group-hover:bg-purple-600/30 transition-all duration-500 pointer-events-none" />
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <span className="text-lg">💎</span>
                <span className="font-bold text-neutral-900 dark:text-white text-base">Apex</span>
              </div>
              <span className="text-sm">👑</span>
            </div>
            <p className="text-xs text-neutral-600 dark:text-gray-400 mb-3.5 leading-relaxed">
              Unlock premium features, exclusive events and more.
            </p>
            <Link
              to={`/?premium=1&plan=${currentPremiumPlan.key}`}
              className="w-full py-2.5 px-3 rounded-xl bg-gradient-to-r from-[#8B5CF6] to-[#9333EA] hover:from-[#7C3AED] hover:to-[#8B5CF6] text-white text-xs font-semibold flex items-center justify-center gap-1.5 shadow-md shadow-purple-700/30 hover:shadow-purple-700/50 transition-all active:scale-[0.98]"
            >
              Upgrade Now <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>

          {/* Create Button */}
          <Link
            to="/create"
            className="w-full py-3 px-4 rounded-xl bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] hover:from-[#DB2777] hover:to-[#7C3AED] text-white text-sm font-semibold flex items-center justify-center gap-2 shadow-lg shadow-pink-600/20 hover:shadow-pink-600/35 transition-all duration-200 active:scale-[0.98] mb-4"
          >
            <Plus size={16} />
            <span>Create</span>
          </Link>
        </nav>

        {/* Sign Out Section at bottom */}
        <div className="border-t border-neutral-200 dark:border-white/[0.07] pt-3 mt-auto">
          <button
            onClick={() => setIsLogoutConfirmOpen(true)}
            className="flex items-center w-full px-4 py-3 rounded-xl transition-all duration-200 hover:bg-red-50 dark:hover:bg-red-900/20 group"
            aria-label="Sign out pointer"
          >
            <div className="w-9 h-9 rounded-full overflow-hidden bg-red-100 dark:bg-red-900/30 flex items-center justify-center ring-2 ring-transparent group-hover:ring-red-400/30 transition-all">
              <LogOut size={16} className="text-red-600 dark:text-red-400" />
            </div>
            <div className="ml-3 flex-1 min-w-0 text-left">
              <p className="text-sm font-semibold text-neutral-900 dark:text-white truncate">
                Sign Out
              </p>
              <p className="text-xs text-neutral-500 dark:text-gray-400">Log out of your account</p>
            </div>
          </button>
        </div>
      </div>

      {/* Sign Out Confirmation Dialog */}
      <ConfirmationDialog
        isOpen={isLogoutConfirmOpen}
        onClose={() => setIsLogoutConfirmOpen(false)}
        onConfirm={async () => {
          try {
            await signOut();
            navigate('/login');
          } catch (error) {
            console.error('Sign out failed:', error);
          }
        }}
        title="Log Out?"
        message="Are you sure you want to log out of your Crevvi account?"
        confirmText="Log Out"
        cancelText="Cancel"
        type="warning"
      />
    </aside>
  );
};

export default LeftSidebar;
